import React from "react"; // Removed useState import
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux"; // Import Redux hooks
import { useGetCartQuery } from "../../../cart/api/cartApi";
import LanguageSwitcher from "../../language/components/LanguageSwitcher";
import Offcanvas from "./Offcanvas";
import SearchPanel from "./searchPanel/SearchPanel";
import CartPanel from "./cartPanel/CartPanel";
import {
  openHeaderActionsOffcanvas,
  closeHeaderActionsOffcanvas,
  setActiveHeaderActionsOffcanvasTab,
  selectIsHeaderActionsOffcanvasOpen,
  selectActiveHeaderActionsOffcanvasTab,
  type HeaderOffcanvasTab, // Import type
} from "../../headerUiSlice";
import type { AppDispatch } from "../../../../app/store"; // Import AppDispatch type

const SearchIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="header-action-icon search-icon"
  >
    <circle cx="11" cy="11" r="8"></circle>
    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
  </svg>
);

const CartIcon = ({ count }: { count?: number }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="header-action-icon cart-icon"
  >
    <circle cx="9" cy="21" r="1"></circle>
    <circle cx="20" cy="21" r="1"></circle>
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
  </svg>
);

const HeaderActionsContainer: React.FC = () => {
  const { t, ready: translationsReady } = useTranslation();
  const dispatch = useDispatch<AppDispatch>(); // Get dispatch function

  // Get state from Redux store using selectors
  const isOffcanvasOpen = useSelector(selectIsHeaderActionsOffcanvasOpen);
  const activeTab = useSelector(selectActiveHeaderActionsOffcanvasTab);

  // Fetch cart data for the count indicator
  const { data: cart, isLoading: isCartLoading } = useGetCartQuery(undefined, {
    selectFromResult: ({ data, isLoading }) => ({
      data: data ? { itemsCount: data.itemsCount } : undefined,
      isLoading,
    }),
    refetchOnFocus: true,
  });
  const cartItemCount = cart?.itemsCount ?? 0;

  // Handlers now dispatch Redux actions
  const handleOpenOffcanvas = (tab: HeaderOffcanvasTab) => {
    dispatch(openHeaderActionsOffcanvas(tab));
  };

  const handleCloseOffcanvas = () => {
    dispatch(closeHeaderActionsOffcanvas());
  };

  const handleSetActiveTab = (tab: HeaderOffcanvasTab) => {
    dispatch(setActiveHeaderActionsOffcanvasTab(tab));
  };

  // Only render the component if translations are ready
  if (!translationsReady) {
    return null; // Or <div className="header-actions-loading">Loading...</div>
  }

  return (
    <>
      <div className="header-actions">
        {/* Search Button */}
        <button
          className="header-action-button"
          aria-label={t("common.search")}
          onClick={() => handleOpenOffcanvas("search")}
        >
          <SearchIcon />
        </button>

        {/* Cart Button */}
        <button
          className="header-action-button header-action-button--cart"
          aria-label={t("cart.title")}
          onClick={() => handleOpenOffcanvas("cart")}
        >
          <CartIcon count={cartItemCount} />
          {/* Cart Count Indicator */}
          {!isCartLoading && cartItemCount > 0 && (
            <span className="cart-count-badge">{cartItemCount}</span>
          )}
        </button>

        {/* Language Switcher remains outside */}
        <LanguageSwitcher className="header-language-switcher" />
      </div>

      {/* Offcanvas Component */}
      <Offcanvas
        isOpen={isOffcanvasOpen}
        onClose={handleCloseOffcanvas}
        activeTab={activeTab}
        onTabClick={handleSetActiveTab}
      >
        {/* Content based on active tab */}
        <div
          id="offcanvas-search-panel"
          role="tabpanel"
          aria-labelledby="offcanvas-search-tab"
          hidden={activeTab !== "search"}
          className="offcanvas-panel-content"
        >
          <SearchPanel onClose={handleCloseOffcanvas} />
        </div>
        <div
          id="offcanvas-cart-panel"
          role="tabpanel"
          aria-labelledby="offcanvas-cart-tab"
          hidden={activeTab !== "cart"}
          className="offcanvas-panel-content"
        >
          {isOffcanvasOpen && activeTab === "cart" && <CartPanel />}
        </div>
      </Offcanvas>
    </>
  );
};

export default HeaderActionsContainer;
